import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config/api';

const RemarkModal = ({ show, onHide, onSave, initialValue, conditions, netsuiteEntityId }) => {
  const [checkboxes, setCheckboxes] = useState({});
  const [additionalRemark, setAdditionalRemark] = useState('');
  const [customerLongmemos, setCustomerLongmemos] = useState([]);
  const [loadingLongmemos, setLoadingLongmemos] = useState(false);

  const editSession = useRef({ open: false, entityId: null });

  // Initialize once per opening/customer. New parent props while editing
  // must not replace the user's draft or reset their checkbox selections.
  useEffect(() => {
    const opening = show && (!editSession.current.open || editSession.current.entityId !== netsuiteEntityId);
    editSession.current = { open: show, entityId: netsuiteEntityId };
    if (opening) {
      if (initialValue && initialValue.length > 0) {
        const allParts = initialValue.split(',').map(str => str.trim()).filter(Boolean);
        // Flatten all condition names for easy matching
        const conditionsFlat = [];
        Object.entries(conditions).forEach(([group, items]) => {
          items.forEach(item => conditionsFlat.push({ ...item, group }));
        });

        const newCheckboxes = {};
        let remarkParts = [];
        allParts.forEach(part => {
          const found = conditionsFlat.find(cond => part === cond.condition_name);
          if (found) {
            newCheckboxes[found.id] = true;
          } else {
            remarkParts.push(part);
          }
        });

        setCheckboxes(newCheckboxes);
        setAdditionalRemark(remarkParts.join(', '));
      } else {
        setCheckboxes({});
        setAdditionalRemark('');
      }

    }
  }, [show, initialValue, conditions, netsuiteEntityId]);

  useEffect(() => {
    let active = true;
    setCustomerLongmemos([]);
    setLoadingLongmemos(false);
    if (!show || !netsuiteEntityId) return undefined;

    const fetchCustomerLongmemos = async () => {
      setLoadingLongmemos(true);
      try {
        const response = await axios.get(`${API_URL}/longmemos/${netsuiteEntityId}`);
        if (active && response.data.success) {
          setCustomerLongmemos(response.data.longmemos);
        }
      } catch (error) {
        if (active) console.error('Error fetching longmemos:', error);
      } finally {
        if (active) setLoadingLongmemos(false);
      }
    };
    fetchCustomerLongmemos();
    return () => { active = false; };
  }, [show, netsuiteEntityId]);

  const handleCheckboxChange = (id, checked) => {
    setCheckboxes(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  const handleLongmemoSelect = (e) => {
    const selectedMemo = e.target.value;
    if (selectedMemo) {
      // If there's already text, append with comma, otherwise just set it
      if (additionalRemark.trim()) {
        setAdditionalRemark(additionalRemark + ', ' + selectedMemo);
      } else {
        setAdditionalRemark(selectedMemo);
      }
      // Reset the dropdown selection
      e.target.value = '';
    }
  };

  const handleSave = () => {
    // Gather checked conditions
    const selectedConditions = [];
    Object.entries(conditions).forEach(([groupName, items]) => {
      items.forEach(item => {
        if (checkboxes[item.id]) {
          selectedConditions.push(item.condition_name);
        }
      });
    });

    let result = selectedConditions.join(', ');
    if (additionalRemark && additionalRemark.trim()) {
      result = result ? result + ', ' + additionalRemark.trim() : additionalRemark.trim();
    }
    onSave(result);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>เลือกเงื่อนไขและหมายเหตุ</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <h6 className="mb-3">เงื่อนไขการเปิดบิล</h6>
          {Object.entries(conditions).map(([groupName, items]) => (
            <div className="card mb-3 shadow-sm border-0" key={groupName}>
              <div className="card-header bg-primary text-white py-2">{groupName}</div>
              <div className="card-body">
                {items
                  .filter(item => !!item.condition_name && item.condition_name !== '0')
                  .map(item => (
                    <div key={item.id} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`cond-${item.id}`}
                        label={item.condition_name}
                        checked={!!checkboxes[item.id]}
                        onChange={e => handleCheckboxChange(item.id, e.target.checked)}
                        className="mb-1"
                      />
                    </div>
                  ))}
              </div>
            </div>
          ))}

          <div className="mt-4">
            {/* Longmemo Dropdown Section */}
            <div className="mb-3">
              <h6 className="mb-2">
                <i className="bi bi-clock-history me-2 text-info"></i>
                เลือกจากหมายเหตุที่เคยใช้กับลูกค้ารายนี้
              </h6>
              {loadingLongmemos ? (
                <div className="text-center p-3 bg-light rounded">
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  กำลังโหลดหมายเหตุที่เคยใช้...
                </div>
              ) : (
                <Form.Select
                  onChange={handleLongmemoSelect}
                  className="mb-2"
                  disabled={customerLongmemos.length === 0}
                >
                  <option value="">
                    {customerLongmemos.length === 0 
                      ? '-- ไม่มีหมายเหตุที่เคยใช้กับลูกค้ารายนี้ --' 
                      : '-- เลือกหมายเหตุที่เคยใช้ (คลิกเพื่อเพิ่มลงในช่องด้านล่าง) --'
                    }
                  </option>
                  {customerLongmemos.map((memo, index) => (
                    <option key={index} value={memo.long_memo}>
                      {memo.long_memo.length > 80 
                        ? memo.long_memo.substring(0, 80) + '...' 
                        : memo.long_memo
                      }
                    </option>
                  ))}
                </Form.Select>
              )}
              {customerLongmemos.length > 0 && (
                <Form.Text className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  มี {customerLongmemos.length} หมายเหตุที่เคยใช้กับลูกค้ารายนี้
                </Form.Text>
              )}
            </div>

            {/* Manual Input Section */}
            <h6>หมายเหตุเพิ่มเติม (จะต่อท้ายด้วย comma)</h6>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="ระบุหมายเหตุเพิ่มเติม หรือเลือกจากด้านบนแล้วแก้ไขเพิ่มเติม"
              value={additionalRemark}
              onChange={e => setAdditionalRemark(e.target.value)}
            />
            <Form.Text muted>
              <i className="bi bi-info-circle me-1"></i>
              หมายเหตุนี้จะถูกต่อท้ายรายการด้วย comma • สามารถเลือกจากรายการด้านบนแล้วแก้ไขเพิ่มเติมได้
            </Form.Text>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          ยกเลิก
        </Button>
        <Button variant="primary" onClick={handleSave}>
          <i className="bi bi-save me-2"></i>
          บันทึก
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RemarkModal;
